import type { StandardServicePageContent } from "~/content/servicePage";

export const ElectricalMeasurementsContent: StandardServicePageContent = {
  heroTitle: "Pomiary elektryczne",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Kiedy potrzebne",
      value: "Przy odbiorze, kontroli 5-letniej i po zmianach",
      description:
        "Pomiary zleca się po remoncie, rozbudowie instalacji, awarii, zalaniu lub przepięciu. Często są też potrzebne przed wynajmem, sprzedażą albo odbiorem robót.",
    },
    {
      label: "Co obejmują",
      value: "Izolację, PE, pętlę zwarcia, RCD i uziemienie",
      description:
        "Zakres zależy od obiektu i celu wizyty. W praktyce bada się te elementy, które realnie potwierdzają bezpieczeństwo instalacji i skuteczność ochrony przeciwporażeniowej.",
    },
    {
      label: "Jaki dokument",
      value: "Protokół z wynikami i oceną instalacji",
      description:
        "Dobry protokół zawiera nie tylko liczby, ale też wnioski: czy instalacja nadaje się do użytkowania, które obwody wymagają naprawy i jakie badania warto rozszerzyć.",
    },
    {
      label: "Ile kosztują",
      value: "Zwykle 200-400 zł za małe mieszkanie",
      description:
        "Dom lub lokal z większą liczbą obwodów wycenia się wyżej, zwykle od około 300 zł wzwyż. Na cenę wpływa liczba rozdzielnic, zakres pomiarów i miasto realizacji.",
    },
  ],
  faqTitle: "Najczęstsze pytania o pomiary elektryczne",
  sections: [
    {
      title: "Na czym polegają pomiary elektryczne i kiedy warto je zlecić",
      paragraphs: [
        `Pomiary elektryczne to zestaw badań, które pokazują, czy instalacja jest bezpieczna w codziennym użytkowaniu i czy zabezpieczenia działają zgodnie z wymaganiami technicznymi. Dzięki nim można potwierdzić stan instalacji po wykonaniu prac, przy odbiorze obiektu albo w ramach kontroli okresowej.`,
        `Najczęściej pomiary zleca się przy odbiorze nowej instalacji, po remoncie, rozbudowie rozdzielni, po awarii, zalaniu lub przepięciu. To również ważny etap kontroli co najmniej raz na 5 lat oraz częsty wymóg przy sprzedaży, wynajmie i porządkowaniu dokumentacji budynku.`,
        `W praktyce nie chodzi o samą tabelę z wynikami. Liczy się odpowiedź, czy instalacja może być dalej bezpiecznie użytkowana, które obwody wymagają poprawy i czy problem dotyczy pojedynczego elementu, czy całego układu.`,
      ],
    },
    {
      title: "Jakie pomiary wykonuje się w instalacji elektrycznej",
      paragraphs: [
        `Najczęściej wykonuje się pomiar rezystancji izolacji, ciągłości przewodów ochronnych, impedancji pętli zwarcia, testy wyłączników różnicowoprądowych oraz ocenę uziemienia. Taki zestaw pozwala sprawdzić zarówno stan przewodów, jak i skuteczność ochrony przeciwporażeniowej.`,
        `Zakres badań dobiera się do celu wizyty. Przy odbiorze nowej instalacji lub po większej modernizacji pomiary są zwykle szersze niż przy samej diagnostyce wybranego problemu, bo mają potwierdzić poprawność całego układu, a nie tylko jednego obwodu.`,
        `W starszych budynkach szczególnie często wychodzą problemy z pogorszoną izolacją, nieczytelnym podziałem obwodów, słabym stanem połączeń ochronnych albo błędami po dokładanych przez lata przeróbkach. To właśnie takie przypadki trudno wykryć bez rzetelnych pomiarów.`,
      ],
    },
    {
      title: "Pomiary elektryczne w domu, mieszkaniu i lokalu",
      paragraphs: [
        `W mieszkaniu pomiary skupiają się zwykle na rozdzielnicy, obwodach gniazd i oświetlenia oraz skuteczności ochrony w samym lokalu. W domu jednorodzinnym dochodzą garaż, kotłownia, obwody zewnętrzne, pompa ciepła, fotowoltaika albo ładowarka EV, więc zakres bywa szerszy.`,
        `W blokach i budynkach wielorodzinnych warto pamiętać o podziale odpowiedzialności. Właściciel mieszkania odpowiada za instalację w lokalu, a wspólnota lub zarządca za części wspólne, piony i rozdzielnie główne. Dlatego przy większych zleceniach dobrze od razu ustalić, który fragment instalacji ma być objęty protokołem.`,
        `W dużych miastach, gdzie częściej trafiają się starsze kamienice i lokale po wielu etapach modernizacji, zakres pomiarów bywa szerszy niż w nowym budownictwie. To właśnie lokalny stan techniczny instalacji, a nie sam adres, często najmocniej wpływa na czas pracy elektryka.`,
      ],
    },
    {
      title: "Cena pomiarów elektrycznych i od czego zależy",
      paragraphs: [
        `Cena pomiarów elektrycznych zależy głównie od liczby obwodów, rodzaju obiektu, zakresu badań i tego, czy chodzi o odbiór, kontrolę okresową czy diagnostykę po usterce. Małe mieszkanie z prostą rozdzielnią wycenia się inaczej niż dom, lokal usługowy albo budynek z rozbudowaną infrastrukturą.`,
        `Na koszt wpływa również to, czy pomiary obejmują tylko wybrane obwody, czy pełen zakres wraz z uziemieniem, rozdzielnią, testami RCD i bardziej szczegółowym protokołem. Im więcej punktów pomiarowych i im dokładniejsza dokumentacja, tym większy nakład pracy po stronie wykonawcy.`,
        `Porównując oferty, warto patrzeć nie tylko na cenę końcową, ale też na to, co naprawdę wchodzi w zakres. W praktyce lokalny rynek, dojazd i dostępność terminów mają znaczenie, ale jeszcze ważniejsze jest to, czy wycena obejmuje pełny protokół oraz realną ocenę bezpieczeństwa instalacji.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług pomiarów elektrycznych",
  scopeItems: [
    "Pomiar rezystancji izolacji",
    "Pomiar impedancji pętli zwarcia",
    "Test wyłączników RCD",
    "Ciągłość przewodów ochronnych",
    "Pomiar uziemienia",
    "Ocena rozdzielnicy",
    "Pomiary odbiorcze po remoncie",
    "Protokół z wynikami",
  ],

  seo: {
    title: "Pomiary elektryczne: cena, zakres, protokół i odbiór instalacji",
    description:
      "Zamów pomiary elektryczne do odbioru, kontroli 5-letniej lub po remoncie. Sprawdź cenę, zakres badań i to, co zawiera protokół.",
  },
};
